import React, { useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormControlLabel,
	Grid,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Skeleton,
	Stack,
	Switch,
	TextField,
	Typography,
} from '@mui/material';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import PaidIcon from '@mui/icons-material/Paid';
import TuneIcon from '@mui/icons-material/Tune';
import { SectionInfoTooltip } from 'features/salesDashboard/components/SectionInfoTooltip';
import { useCreditRulesCatalog, useCreditTermPreview } from '../../hooks/useCreditRules';
import { extractCreditRulesError, patchCreditTerm, postCreditTerm } from '../../services';
import { CorteTipo, DiaSemana, ICreditTerm, ICreditTermPayload, ReglaVencimiento } from '../../types';
import { formatFecha } from '../../helpers/describe';
import { dosEjesTooltip } from '../../constants';

/** Estado del formulario: strings para los inputs, se castea al enviar. */
interface FormState {
	name: string;
	description: string;
	corteTipo: CorteTipo;
	corteDiaSemana: DiaSemana | '';
	corteDiasMes: string;
	vencRegla: ReglaVencimiento | '';
	vencDiasCorridos: string;
	vencDiaDelMes: string;
	vencOffsetMeses: string;
	vencSemana: string;
	vencDiaSemana: DiaSemana | '';
	fechaBase: string;
	ajusteFinDeSemana: string;
	topeDiasAntesViaje: string;
	enabled: boolean;
}

const EMPTY: FormState = {
	name: '',
	description: '',
	corteTipo: 'SIN_CORTE',
	corteDiaSemana: '',
	corteDiasMes: '15, 31',
	vencRegla: 'DIAS_FIJOS',
	vencDiasCorridos: '30',
	vencDiaDelMes: '',
	vencOffsetMeses: '0',
	vencSemana: '',
	vencDiaSemana: '',
	fechaBase: 'FACTURA',
	ajusteFinDeSemana: 'NINGUNO',
	topeDiasAntesViaje: '',
	enabled: true,
};

function fromTerm(term: ICreditTerm): FormState {
	return {
		name: term.name,
		description: term.description ?? '',
		corteTipo: term.corteTipo,
		corteDiaSemana: term.corteDiaSemana ?? '',
		corteDiasMes: (term.corteDiasMes ?? [15, 31]).join(', '),
		vencRegla: term.vencRegla ?? '',
		vencDiasCorridos: term.vencDiasCorridos?.toString() ?? '',
		vencDiaDelMes: term.vencDiaDelMes?.toString() ?? '',
		vencOffsetMeses: (term.vencOffsetMeses ?? 0).toString(),
		vencSemana: term.vencSemana ?? '',
		vencDiaSemana: term.vencDiaSemana ?? '',
		fechaBase: term.fechaBase,
		ajusteFinDeSemana: term.ajusteFinDeSemana,
		topeDiasAntesViaje: term.topeDiasAntesViaje?.toString() ?? '',
		enabled: term.enabled,
	};
}

/** "15, 31" → [15, 31]. Ignora lo que no sea número. */
function parseDiasMes(s: string): number[] {
	return s
		.split(',')
		.map((x) => Number(x.trim()))
		.filter((n) => Number.isFinite(n) && n >= 1 && n <= 31);
}

const numOrUndef = (s: string): number | undefined => {
	const n = Number(s);
	return s.trim() !== '' && Number.isFinite(n) ? n : undefined;
};

/** Estado del form → payload de la API (solo los campos que la regla usa). */
function toPayload(f: FormState, includeEnabled: boolean): ICreditTermPayload {
	const esQuincenal = f.corteTipo === 'QUINCENAL';
	const esSemanal = f.corteTipo === 'SEMANAL';
	const usaOffset =
		f.vencRegla === 'ULTIMO_DIA_MES' || f.vencRegla === 'DIA_DEL_MES' || f.vencRegla === 'ENESIMO_DIA_SEMANA';

	return {
		name: f.name.trim(),
		description: f.description.trim() || undefined,
		corteTipo: f.corteTipo,
		corteDiaSemana: esSemanal && f.corteDiaSemana ? f.corteDiaSemana : undefined,
		corteDiasMes: esQuincenal ? parseDiasMes(f.corteDiasMes) : undefined,
		vencRegla: f.vencRegla || undefined,
		vencDiasCorridos: f.vencRegla === 'DIAS_FIJOS' ? numOrUndef(f.vencDiasCorridos) : undefined,
		vencDiaDelMes: f.vencRegla === 'DIA_DEL_MES' ? numOrUndef(f.vencDiaDelMes) : undefined,
		vencOffsetMeses: usaOffset ? numOrUndef(f.vencOffsetMeses) ?? 0 : 0,
		vencSemana: f.vencRegla === 'ENESIMO_DIA_SEMANA' && f.vencSemana ? f.vencSemana : undefined,
		vencDiaSemana: f.vencRegla === 'ENESIMO_DIA_SEMANA' && f.vencDiaSemana ? f.vencDiaSemana : undefined,
		fechaBase: f.fechaBase as ICreditTermPayload['fechaBase'],
		ajusteFinDeSemana: f.ajusteFinDeSemana as ICreditTermPayload['ajusteFinDeSemana'],
		topeDiasAntesViaje: numOrUndef(f.topeDiasAntesViaje),
		...(includeEnabled ? { enabled: f.enabled } : {}),
	};
}

interface CreditTermFormDialogProps {
	open: boolean;
	onClose: () => void;
	/** Si viene, es edición; si no, alta. */
	term?: ICreditTerm | null;
	onSaved: () => void;
}

/**
 * Alta y edición de una condición del catálogo.
 *
 * Dos decisiones de diseño:
 *  1. **Solo se muestra lo que la regla usa.** Elegir "días fijos" no debería
 *     mostrar campos de "día de la semana": cada tipo revela sus parámetros.
 *  2. **Preview en vivo.** Mientras se arma la condición, el backend calcula
 *     "si facturás hoy, vence el ..." — y si falta un parámetro, dice cuál.
 *     Es la red de seguridad contra cargar mal un plazo de cobro.
 */
export function CreditTermFormDialog({ open, onClose, term, onSaved }: CreditTermFormDialogProps): JSX.Element {
	const esEdicion = Boolean(term);
	const { data: catalog, loading: catalogLoading } = useCreditRulesCatalog();
	const [form, setForm] = useState<FormState>(term ? fromTerm(term) : EMPTY);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Al abrir con otra condición (o pasar de editar a crear), recargar el form.
	const termKey = term?.id ?? 'nuevo';
	React.useEffect(() => {
		if (open) {
			setForm(term ? fromTerm(term) : EMPTY);
			setError(null);
		}
		// `term` se resuelve por `termKey`: no va en deps para no recargar el
		// formulario en cada render del padre y perder lo que el usuario escribió.
	}, [open, termKey]);

	const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((prev) => ({ ...prev, [k]: v }));

	// El preview solo necesita la forma de la regla, no el nombre.
	const previewPayload = useMemo(() => {
		const p = toPayload(form, false);
		delete p.name;
		delete p.description;
		return p;
	}, [form]);
	const {
		data: preview,
		error: previewError,
		loading: previewLoading,
	} = useCreditTermPreview(open ? previewPayload : null);

	const usaOffset =
		form.vencRegla === 'ULTIMO_DIA_MES' || form.vencRegla === 'DIA_DEL_MES' || form.vencRegla === 'ENESIMO_DIA_SEMANA';

	const handleSave = async () => {
		if (!form.name.trim()) {
			setError('Poné un nombre para la condición.');
			return;
		}
		setSaving(true);
		setError(null);
		try {
			if (term) await patchCreditTerm(term.id, toPayload(form, true));
			else await postCreditTerm(toPayload(form, false));
			onSaved();
			onClose();
		} catch (err) {
			setError(extractCreditRulesError(err));
		} finally {
			setSaving(false);
		}
	};

	const diasSemana = catalog?.diasSemana ?? [];
	const ocurrencias = catalog?.ocurrenciasSemana ?? ['1', '2', '3', '4', 'ULTIMA'];

	return (
		<Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
			<DialogTitle>
				<Box display="inline-flex" alignItems="center" gap={0.75}>
					{esEdicion ? `Editar condición “${term?.name}”` : 'Nueva condición de crédito'}
					<SectionInfoTooltip content={dosEjesTooltip} />
				</Box>
			</DialogTitle>
			<DialogContent>
				{catalogLoading ? (
					<Skeleton variant="rectangular" height={320} />
				) : (
					<Stack spacing={2.5} mt={0.5}>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={5}>
								<TextField
									label="Nombre"
									size="small"
									fullWidth
									required
									value={form.name}
									onChange={(e) => set('name', e.target.value)}
									placeholder="Ej: 30 días"
									helperText="Es lo que se ve en la ficha del cliente"
								/>
							</Grid>
							<Grid item xs={12} sm={7}>
								<TextField
									label="Descripción (opcional)"
									size="small"
									fullWidth
									value={form.description}
									onChange={(e) => set('description', e.target.value)}
									placeholder="Aclaración para quien la asigna"
								/>
							</Grid>
						</Grid>

						{/* ── Eje 1: corte ─────────────────────────────────── */}
						<Paper variant="outlined" sx={{ p: 2 }}>
							<Box display="flex" alignItems="center" gap={0.75} mb={1.5}>
								<EventRepeatIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
								<Typography variant="subtitle2" fontWeight={600}>
									Cuándo se factura
								</Typography>
							</Box>
							<Grid container spacing={2}>
								<Grid item xs={12} sm={6}>
									<FormControl size="small" fullWidth>
										<InputLabel>Corte</InputLabel>
										<Select
											value={form.corteTipo}
											label="Corte"
											onChange={(e) => set('corteTipo', e.target.value as CorteTipo)}
										>
											{(catalog?.cortes ?? []).map((c) => (
												<MenuItem key={c.id} value={c.id}>
													{c.label}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>

								{form.corteTipo === 'SEMANAL' && (
									<Grid item xs={12} sm={6}>
										<FormControl size="small" fullWidth>
											<InputLabel>Día de cierre</InputLabel>
											<Select
												value={form.corteDiaSemana}
												label="Día de cierre"
												onChange={(e) => set('corteDiaSemana', e.target.value as DiaSemana)}
											>
												{diasSemana.map((d) => (
													<MenuItem key={d} value={d}>
														{d.charAt(0) + d.slice(1).toLowerCase()}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									</Grid>
								)}

								{form.corteTipo === 'QUINCENAL' && (
									<Grid item xs={12} sm={6}>
										<TextField
											label="Días de corte del mes"
											size="small"
											fullWidth
											value={form.corteDiasMes}
											onChange={(e) => set('corteDiasMes', e.target.value)}
											placeholder="15, 31"
											helperText="Separados por coma. El 31 significa “último día del mes”."
										/>
									</Grid>
								)}
							</Grid>
							{form.corteTipo === 'PERSONALIZADO' && (
								<Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>
									Con corte personalizado el sistema <strong>no calcula</strong> el vencimiento: al facturar se avisa
									que hay que definirlo a mano.
								</Alert>
							)}
						</Paper>

						{/* ── Eje 2: vencimiento ───────────────────────────── */}
						<Paper variant="outlined" sx={{ p: 2 }}>
							<Box display="flex" alignItems="center" gap={0.75} mb={1.5}>
								<PaidIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
								<Typography variant="subtitle2" fontWeight={600}>
									Cuándo vence
								</Typography>
							</Box>
							<Grid container spacing={2}>
								<Grid item xs={12} sm={6}>
									<FormControl size="small" fullWidth>
										<InputLabel>Regla</InputLabel>
										<Select
											value={form.vencRegla}
											label="Regla"
											onChange={(e) => set('vencRegla', e.target.value as ReglaVencimiento | '')}
										>
											<MenuItem value="">
												<em>Sin definir (se completa a mano al facturar)</em>
											</MenuItem>
											{(catalog?.vencimientos ?? []).map((v) => (
												<MenuItem key={v.id} value={v.id}>
													{v.label}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>

								{form.vencRegla === 'DIAS_FIJOS' && (
									<Grid item xs={12} sm={6}>
										<TextField
											label="Días corridos"
											size="small"
											fullWidth
											type="number"
											value={form.vencDiasCorridos}
											onChange={(e) => set('vencDiasCorridos', e.target.value)}
											helperText="0 = contado (vence el mismo día)"
										/>
									</Grid>
								)}

								{form.vencRegla === 'DIA_DEL_MES' && (
									<Grid item xs={12} sm={6}>
										<TextField
											label="Día del mes"
											size="small"
											fullWidth
											type="number"
											value={form.vencDiaDelMes}
											onChange={(e) => set('vencDiaDelMes', e.target.value)}
											helperText="Si el mes no llega a ese día, se usa el último (30 en febrero → 28)"
										/>
									</Grid>
								)}

								{form.vencRegla === 'ENESIMO_DIA_SEMANA' && (
									<>
										<Grid item xs={12} sm={3}>
											<FormControl size="small" fullWidth>
												<InputLabel>Ocurrencia</InputLabel>
												<Select
													value={form.vencSemana}
													label="Ocurrencia"
													onChange={(e) => set('vencSemana', e.target.value)}
												>
													{ocurrencias.map((o) => (
														<MenuItem key={o} value={o}>
															{o === 'ULTIMA' ? 'Último' : `${o}º`}
														</MenuItem>
													))}
												</Select>
											</FormControl>
										</Grid>
										<Grid item xs={12} sm={3}>
											<FormControl size="small" fullWidth>
												<InputLabel>Día</InputLabel>
												<Select
													value={form.vencDiaSemana}
													label="Día"
													onChange={(e) => set('vencDiaSemana', e.target.value as DiaSemana)}
												>
													{diasSemana.map((d) => (
														<MenuItem key={d} value={d}>
															{d.charAt(0) + d.slice(1).toLowerCase()}
														</MenuItem>
													))}
												</Select>
											</FormControl>
										</Grid>
									</>
								)}

								{usaOffset && (
									<Grid item xs={12} sm={6}>
										<FormControl size="small" fullWidth>
											<InputLabel>Mes</InputLabel>
											<Select
												value={form.vencOffsetMeses}
												label="Mes"
												onChange={(e) => set('vencOffsetMeses', e.target.value)}
											>
												<MenuItem value="0">Del mismo mes</MenuItem>
												<MenuItem value="1">Del mes siguiente</MenuItem>
												<MenuItem value="2">A dos meses</MenuItem>
											</Select>
										</FormControl>
									</Grid>
								)}

								<Grid item xs={12} sm={6}>
									<FormControl size="small" fullWidth>
										<InputLabel>Contado desde</InputLabel>
										<Select
											value={form.fechaBase}
											label="Contado desde"
											onChange={(e) => set('fechaBase', e.target.value)}
										>
											{(catalog?.fechasBase ?? []).map((f) => (
												<MenuItem key={f.id} value={f.id}>
													{f.label}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>
							</Grid>
						</Paper>

						{/* ── Modificadores ────────────────────────────────── */}
						<Paper variant="outlined" sx={{ p: 2 }}>
							<Box display="flex" alignItems="center" gap={0.75} mb={1.5}>
								<TuneIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
								<Typography variant="subtitle2" fontWeight={600}>
									Excepciones
								</Typography>
							</Box>
							<Grid container spacing={2}>
								<Grid item xs={12} sm={6}>
									<FormControl size="small" fullWidth>
										<InputLabel>Si cae fin de semana</InputLabel>
										<Select
											value={form.ajusteFinDeSemana}
											label="Si cae fin de semana"
											onChange={(e) => set('ajusteFinDeSemana', e.target.value)}
										>
											{(catalog?.ajustesFinDeSemana ?? []).map((a) => (
												<MenuItem key={a.id} value={a.id}>
													{a.label}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>
								<Grid item xs={12} sm={6}>
									<TextField
										label="Tope: días antes del viaje (opcional)"
										size="small"
										fullWidth
										type="number"
										value={form.topeDiasAntesViaje}
										onChange={(e) => set('topeDiasAntesViaje', e.target.value)}
										helperText="El vencimiento nunca cae después de N días antes de la salida"
									/>
								</Grid>
							</Grid>
							{esEdicion && (
								<FormControlLabel
									sx={{ mt: 1 }}
									control={
										<Switch checked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} size="small" />
									}
									label={
										<Typography variant="body2">
											Habilitada{' '}
											<Typography component="span" variant="caption" color="text.secondary">
												(si la deshabilitás no se puede asignar a clientes nuevos)
											</Typography>
										</Typography>
									}
								/>
							)}
						</Paper>

						{/* ── Preview en vivo ──────────────────────────────── */}
						<Divider />
						<Box>
							<Typography variant="caption" color="text.secondary" display="block" mb={0.75}>
								Vista previa — si facturás hoy con esta condición
							</Typography>
							{previewLoading ? (
								<Skeleton variant="rectangular" height={48} sx={{ borderRadius: 1 }} />
							) : previewError ? (
								<Alert severity="warning" sx={{ py: 0.5 }}>
									{previewError}
								</Alert>
							) : preview?.resultado?.requiereRevisionManual ? (
								<Alert severity="info" sx={{ py: 0.5 }}>
									Esta condición no calcula el vencimiento sola: al facturar se pide completarlo a mano.
								</Alert>
							) : preview?.resultado?.fechaVencFinal ? (
								<Alert severity="success" sx={{ py: 0.5 }}>
									<Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
										<Typography variant="body2">
											Vencería el <strong>{formatFecha(preview.resultado.fechaVencFinal)}</strong>
										</Typography>
										{preview.resultado.diasPlazoEfectivo != null && (
											<Chip
												label={`${preview.resultado.diasPlazoEfectivo} días de plazo`}
												size="small"
												variant="outlined"
											/>
										)}
										{preview.resultado.aplicoAjusteFinDeSemana && (
											<Chip label="Movida por fin de semana" size="small" variant="outlined" />
										)}
									</Box>
									{preview.resultado.alertas.map((a) => (
										<Typography key={a} variant="caption" color="warning.main" display="block">
											⚠️ {a}
										</Typography>
									))}
								</Alert>
							) : null}
						</Box>

						{error && <Alert severity="error">{error}</Alert>}
					</Stack>
				)}
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>
					Cancelar
				</Button>
				<Button
					onClick={handleSave}
					variant="contained"
					disabled={saving || !form.name.trim()}
					sx={{ textTransform: 'none' }}
				>
					{saving ? <CircularProgress size={18} /> : esEdicion ? 'Guardar cambios' : 'Crear condición'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
